import { DeleteConfirmationDialog } from '@seek/profile-edit-ui';
import { useTranslations } from '@vocab/react';
import { Strong } from 'braid-design-system';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  useFindNextDefaultResume,
  useDeleteResume,
  useResumes,
  useMakeDefaultResume,
} from 'src/components/Profile/Resume/data';
import type { Origin } from 'src/components/Profile/Resume/types';
import { useAppConfig } from 'src/modules/appConfig';

import { cancelDeleteResume } from '../../../../actionCreators';

import translations from './.vocab';

interface Props {
  id: string;
  origin: Origin;
  open: boolean;
}

export const ConfirmDelete = ({ id, open, origin }: Props) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const [deleteResume] = useDeleteResume();
  const [makeDefaultResume] = useMakeDefaultResume();
  const { language: languageCode } = useAppConfig();
  const { resumes, defaultResumeId } = useResumes();

  const showDefaultResumeDialog = defaultResumeId === id && resumes.length > 1;

  const nextDefaultResume = useFindNextDefaultResume();
  const customDefaultResumeDeleteMessage = t(
    'Your latest available resume fileName will be set as default.',
    {
      fileName: nextDefaultResume?.resumeName || '',
      Strong: (v) => <Strong>{v}</Strong>,
    },
  );

  const onDelete = useCallback(() => {
    deleteResume({ resumeId: { id }, languageCode }, { origin });
    if (showDefaultResumeDialog && nextDefaultResume) {
      makeDefaultResume(
        { resumeId: { id: nextDefaultResume.resumeId }, languageCode },
        { origin },
      );
    }
  }, [
    deleteResume,
    id,
    languageCode,
    makeDefaultResume,
    nextDefaultResume,
    origin,
    showDefaultResumeDialog,
  ]);

  const onCancel = useCallback(() => {
    dispatch(cancelDeleteResume());
  }, [dispatch]);

  return (
    <DeleteConfirmationDialog
      displayDialog={open}
      setDisplayDialog={onCancel}
      onDelete={onDelete}
      cardName={showDefaultResumeDialog ? t('default resumé') : t('resumé')}
      dataAutomationSuffix={`resume-${id}`}
      {...(showDefaultResumeDialog && {
        customMessage: customDefaultResumeDeleteMessage,
      })}
    />
  );
};
